import React, { useEffect, useState } from "react";
import {
  Box,
  Switch,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import { BASE_URL } from "../../../components/BaseUrl";
import toast from "react-hot-toast";
import back from "../../../assets/back.svg";
import CustomText, { textStyles } from "../../../components/CustomText";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../components/CustomButton";
const AutoNotificationsSettings = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    birthday: false,
    workAnniversary: false,
    newEmployee: false,
    jobChange: false,
    adminOnly: false,
    manualPopUp: false,
  });

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { t } = useTranslation("");

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* =======================
     FETCH (GET) SETTINGS
     ======================= */
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `${BASE_URL}/company-admins/notification-settings`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${decryptedToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        // if (!res.ok) throw new Error("Failed to fetch settings");

 const response = await res.json();
console.log("Fetched Settings:", response);

const data = response.data;

setSettings({
  birthday: data?.automatic_notifications?.birthday ?? false,
  workAnniversary:
    data?.automatic_notifications?.work_anniversary ?? false,
  newEmployee:
    data?.automatic_notifications?.new_employee ?? false,
  jobChange:
    data?.automatic_notifications?.job_changes ?? false,
  adminOnly:
    data?.automatic_notifications?.admin_activities ?? false,
  manualPopUp:
    data?.manual_notifications?.enabled ?? false,
});
      } catch (err) {
       toast.error(t("notificationsScreen.loadError"));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (decryptedToken) {
      fetchSettings();
    }
  }, [decryptedToken]);

  /* =======================
     SAVE (POST) SETTINGS
     ======================= */
  const handleSave = async () => {
    try {
      setLoading(true);

      const payload = {
        enable_birthday_notifications: settings.birthday,
        enable_work_anniversary_notifications:
          settings.workAnniversary,
        enable_new_employee_notifications:
          settings.newEmployee,
        enable_job_change_notifications:
          settings.jobChange,
        enable_admin_notifications:
          settings.adminOnly,
        allow_manual_notifications:
          settings.manualPopUp,
      };

      const res = await fetch(
        `${BASE_URL}/company-admins/notification-settings`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      // if (!res.ok) throw new Error("Failed to save settings");

toast.success(t("notificationsScreen.saveSuccess"));
    } catch (err) {
  toast.error(t("notificationsScreen.loadError"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: "#f8f9fb", minHeight: "100vh" }}>
      {/* Back */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
          mb: 3,
        }}
        onClick={onBack}
      >
        <img src={back} style={{ width: 30, height: 30 }} alt="Back" />
        <CustomText
          sx={{
            ...textStyles.h1,
            fontWeight: 500,
            fontSize: "14px",
            color: "#626F86",
          }}
        >
          {t("settings.notificationNameTitle")}
        </CustomText>
      </Box>

      {/* Automatic Notifications */}
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 600,
          color: "#172B4D",
          fontFamily: "Poppins, sans-serif",
          mb: 2,
        }}
      >
        {t("settings.automaticPopups")}
      </Typography>

      <Box
        sx={{
          backgroundColor: "white",
          border: "1px solid #E0E0E0",
          borderRadius: 2,
          py: 1.5,
        }}
      >
        {[
          { key: "birthday", label: t("settings.autoBirthday") },
          {
            key: "workAnniversary",
            label: t("settings.autoWorkAnniversary"),
          },
          { key: "newEmployee", label: t("settings.autoNewEmployee") },
          { key: "jobChange", label: t("settings.autoJobChange") },
          { key: "adminOnly", label: t("settings.autoAdminOnly") },
        ].map((item) => (
          <Box
            key={item.key}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                color: "#333",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {item.label}
            </Typography>
            <Switch
              checked={settings[item.key]}
              onChange={() => handleToggle(item.key)}
              color="primary"
            />
          </Box>
        ))}
      </Box>

      {/* Manual Notifications */}
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 600,
          color: "#172B4D",
          fontFamily: "Poppins, sans-serif",
          mt: 3,
          mb: 1,
        }}
      >
        {t("settings.manualPopups")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "white",
          border: "1px solid #E0E0E0",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            color: "#333",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          {t("settings.manualPopupDescription")}
        </Typography>
        <Switch
          checked={settings.manualPopUp}
          onChange={() => handleToggle("manualPopUp")}
          color="primary"
        />
      </Box>

      {/* Save Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        {/* <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.2,
            borderRadius: 2,
            fontWeight: 500,
          }}
        >
          {loading ? <CircularProgress size={22} /> : t("common.save")}
        </Button> */}
        <CustomButton label={t("common.save")} size='small' onClick={handleSave} disabled={loading} />
      </Box>
    </Box>
  );
};

export default AutoNotificationsSettings;
